import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Plus,
  Trash2,
  MessageSquare,
  Check,
  Database,
  Copy,
  ArrowRight,
} from "lucide-react";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import AssistantMessage, { parseAssistantContent } from "./AssistantMessage";
import AskComposer, { AskLevel, useAskLevel } from "./AskComposer";
import { api } from "../api";
import { useToast } from "../ui/toast";
import { useModal } from "../ui/modal";

interface ChatRow {
  id: string;
  project_id: string | null;
  project_name: string | null;
  dataset_id: string | null;
  dataset_name: string | null;
  title: string;
  model_override: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview: string | null;
}

interface ChatMessage {
  id: string;
  chat_id: string;
  role: "user" | "assistant" | "system";
  content: string;
  sources_json: { row_id?: string }[] | null;
  model: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  created_at: string;
}

interface ChatDetail {
  chat: ChatRow;
  messages: ChatMessage[];
}

// Level → Modell. Easy nutzt Server-Default (gemini-2.5-flash-lite synth /
// 3.1-flash-lite plan); höhere Stufen bleiben als manueller Eskalations-Hebel.
// Der Level selbst liegt zentral in AskComposer (localStorage `sb_ask_level`).
const LEVEL_MODELS: Record<AskLevel, string | null> = {
  easy: null,
  medium: "gemini-3-flash-preview",
  hard: "claude-sonnet-4-6",
  sophisticated: "claude-opus-4-7",
};

function fmtRelative(iso: string) {
  const d = new Date(iso);
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60) return "gerade";
  if (diffSec < 3600) return `vor ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `vor ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 86400 * 7) return `vor ${Math.floor(diffSec / 86400)} T`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

export default function DatasetChatView({
  datasetId,
  datasetName,
}: {
  datasetId: string;
  datasetName: string;
}) {
  const toast = useToast();
  const modal = useModal();
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ChatDetail | null>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [level, setLevel] = useAskLevel();
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const activeModel: string | null = LEVEL_MODELS[level];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.get<ChatRow[]>(
          `/chats?dataset_id=${encodeURIComponent(datasetId)}&limit=50`
        );
        if (cancelled) return;
        setChats(list);
        setActiveChatId(null);
        setDetail(null);
        setQ("");
        setTimeout(() => composerRef.current?.focus(), 50);
      } catch (e) {
        if (!cancelled) toast.err("Chats konnten nicht geladen werden", (e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  useEffect(() => {
    if (!activeChatId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<ChatDetail>(`/chats/${activeChatId}`);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) toast.err("Chat konnte nicht geladen werden", (e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeChatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages?.length]);

  const refreshChatsList = async () => {
    try {
      const list = await api.get<ChatRow[]>(
        `/chats?dataset_id=${encodeURIComponent(datasetId)}&limit=50`
      );
      setChats(list);
    } catch {
      /* silent */
    }
  };

  const newChat = () => {
    setActiveChatId(null);
    setDetail(null);
    setQ("");
    setTimeout(() => composerRef.current?.focus(), 50);
  };

  const deleteChat = async (id: string) => {
    const ok = await modal.confirm({
      title: "Chat löschen?",
      confirmLabel: "Löschen",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/chats/${id}`);
      if (id === activeChatId) {
        setActiveChatId(null);
        setDetail(null);
      }
      await refreshChatsList();
    } catch (e) {
      toast.err("Löschen fehlgeschlagen", (e as Error).message);
    }
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const text = q.trim();
    if (!text) return;

    let chatId = activeChatId;
    setBusy(true);
    setQ("");

    const tempMsg: ChatMessage = {
      id: `tmp_${Date.now()}`,
      chat_id: chatId ?? "",
      role: "user",
      content: text,
      sources_json: null,
      model: null,
      tokens_in: null,
      tokens_out: null,
      created_at: new Date().toISOString(),
    };
    if (detail) {
      setDetail({ ...detail, messages: [...detail.messages, tempMsg] });
    } else {
      setDetail({
        chat: {
          id: "pending",
          project_id: null,
          project_name: null,
          dataset_id: datasetId,
          dataset_name: datasetName,
          title: "Neuer Chat",
          model_override: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          message_count: 1,
          last_message_preview: text,
        },
        messages: [tempMsg],
      });
    }

    try {
      if (!chatId) {
        const created = await api.post<ChatRow>("/chats", {
          dataset_id: datasetId,
        });
        chatId = created.id;
        setActiveChatId(chatId);
      }
      const res = await api.post<ChatDetail>(`/chats/${chatId}/messages`, {
        content: text,
        model: activeModel,
      });
      setDetail(res);
      await refreshChatsList();
    } catch (err) {
      toast.err("Antwort fehlgeschlagen", (err as Error).message);
      if (chatId) {
        try {
          setDetail(await api.get<ChatDetail>(`/chats/${chatId}`));
        } catch {
          /* swallow */
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const headerChatTitle = detail?.chat.title ?? "Neuer Chat";

  return (
    <div className="flex flex-col h-full">
      {/* Header: Breadcrumb + Chat-Picker */}
      <div className="px-5 py-3 border-b border-cream-200 dark:border-ink-600 flex items-center gap-2">
        <Database className="w-4 h-4 text-brand-500 shrink-0" />
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <button className="flex items-center gap-2 text-sm font-medium hover:bg-cream-100 dark:hover:bg-ink-700 rounded-md px-2 py-1 min-w-0">
              <span className="text-ink-50/70 truncate max-w-[160px]">
                {datasetName}
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-ink-50/40 shrink-0" />
              <span className="truncate">{headerChatTitle}</span>
              <ChevronDown className="w-3.5 h-3.5 text-ink-50/40 shrink-0" />
            </button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content
              align="start"
              sideOffset={6}
              className="z-50 min-w-[280px] max-h-[60vh] overflow-y-auto bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg shadow-lg py-1 animate-fade-in"
            >
              <Dropdown.Item
                onSelect={newChat}
                className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-cream-100 dark:hover:bg-ink-700 outline-none focus-visible:ring-2 focus-visible:ring-brand-500 cursor-pointer"
              >
                <Plus className="w-4 h-4 text-brand-500" />
                <span className="text-brand-600 dark:text-brand-400 font-medium">Neuer Chat</span>
              </Dropdown.Item>
              {chats.length > 0 && (
                <Dropdown.Separator className="h-px bg-cream-200 dark:bg-ink-600 my-1" />
              )}
              {chats.map((c) => (
                <Dropdown.Item
                  key={c.id}
                  onSelect={() => setActiveChatId(c.id)}
                  className="group flex items-start gap-2 px-3 py-1.5 text-sm hover:bg-cream-100 dark:hover:bg-ink-700 outline-none focus-visible:ring-2 focus-visible:ring-brand-500 cursor-pointer"
                >
                  <MessageSquare className="w-3.5 h-3.5 text-ink-50/60 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="truncate">{c.title}</span>
                      {c.id === activeChatId && (
                        <Check className="w-3 h-3 text-brand-500 shrink-0" />
                      )}
                    </div>
                    <div className="text-[11px] text-ink-50/60">
                      {c.message_count} Nachrichten · {fmtRelative(c.updated_at)}
                    </div>
                  </div>
                  <button
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      deleteChat(c.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-ink-50/40 hover:text-danger transition-opacity"
                    title="Löschen"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Dropdown.Item>
              ))}
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>

      </div>

      {/* Messages — bei leerem Chat zeigt der Composer INLINE darunter
          (Claude-Style: neue Konversation startet oben/zentriert).
          Sobald Messages da sind, wird der Composer als fixer Footer
          unten gerendert. */}
      <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
        {!detail || detail.messages.length === 0 ? (
          <div className="max-w-2xl mx-auto space-y-6">
            <EmptyState datasetName={datasetName} onPick={(s) => {
              setQ(s);
              setTimeout(() => composerRef.current?.focus(), 0);
            }} />
            <AskComposer
              q={q}
              setQ={setQ}
              onSend={send}
              busy={busy}
              composerRef={composerRef}
              level={level}
              setLevel={setLevel}
              placeholder="Frag deine Tabelle …"
            />
          </div>
        ) : (
          detail.messages.map((m) => <MessageBubble key={m.id} msg={m} />)
        )}
        {busy && (
          <div className="flex items-center gap-2 text-sm text-ink-50/60">
            <Loader2 className="w-4 h-4 animate-spin text-brand-500" /> rechnet …
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer als fixer Footer — nur wenn mindestens 1 Message existiert */}
      {detail && detail.messages.length > 0 && (
        <div className="px-5 pb-5 pt-3 border-t border-cream-200 dark:border-ink-600">
          <AskComposer
            q={q}
            setQ={setQ}
            onSend={send}
            busy={busy}
            composerRef={composerRef}
            level={level}
            setLevel={setLevel}
            placeholder="Frag deine Tabelle …"
          />
        </div>
      )}
    </div>
  );
}

function EmptyState({
  datasetName,
  onPick,
}: {
  datasetName: string;
  onPick: (q: string) => void;
}) {
  // Generische Vorschläge, die auf KnowledgeMap-Datasets gut passen.
  // Später per useEffect dynamisch aus section_index laden.
  const suggestions = [
    "Welche Kategorien gibt es und wie viele Zeilen pro Kategorie?",
    "Was sind die wichtigsten Datenpunkte in Section 3?",
    "Welche Tabellen und Figures gibt es im Dokument?",
    "Fasse die Studien-Ergebnisse zusammen.",
  ];
  return (
    <div className="max-w-2xl mx-auto py-12 space-y-6">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 grid place-items-center shadow-lg shadow-brand-500/20">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <h2 className="font-display text-2xl text-ink tracking-tight">
          Frag <span className="italic text-brand-600 dark:text-brand-400">{datasetName}</span>
        </h2>
        <p className="text-sm text-ink-50/70 max-w-md mx-auto">
          Das Modell sieht die Zeilen direkt und beantwortet Listing-,
          Aggregat- oder Section-Fragen mit exakten Treffern aus der Tabelle.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="group text-left px-4 py-3 rounded-xl bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 hover:border-brand-300 dark:hover:border-brand-700 hover:shadow-sm transition"
          >
            <div className="flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-brand-500 mt-0.5 shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
              <span className="text-[13.5px] text-ink leading-snug">
                {s}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    const text =
      msg.role === "assistant"
        ? (parseAssistantContent(msg.content).answer || msg.content)
        : msg.content;
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (msg.role === "user") {
    // 2026-Best-Practice: KEINE Bubble — full-width Layout mit subtiler
    // cream-Hintergrund-Differenzierung statt Messenger-Style.
    return (
      <div className="group flex gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-cream-100/50 dark:hover:bg-ink-700/30 transition-colors">
        <UserAvatar />
        <div className="flex-1 min-w-0 text-[15px] leading-relaxed whitespace-pre-wrap text-ink pt-0.5">
          {msg.content}
        </div>
        <button
          onClick={handleCopy}
          title={copied ? "Kopiert!" : "Kopieren"}
          className="opacity-0 group-hover:opacity-100 self-start p-1 rounded-md text-ink-50/50 hover:text-ink hover:bg-cream-200 dark:hover:bg-ink-600 transition"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-brand-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    );
  }

  const numSources = msg.sources_json?.length ?? 0;
  const s = parseAssistantContent(msg.content);
  const numHits = s.result_rows?.length ?? 0;
  return (
    <div className="group flex gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-cream-100/30 dark:hover:bg-ink-700/20 transition-colors">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 grid place-items-center shrink-0 shadow-sm shadow-brand-500/20">
        <Sparkles className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <AssistantMessage content={msg.content} />
        <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-50/50">
          {msg.tokens_in != null && (
            <span className="font-mono">
              ⚡ {msg.tokens_in}+{msg.tokens_out ?? 0} tok
            </span>
          )}
          {numHits > 0 && (
            <span className="text-brand-600/70 dark:text-brand-400/70 font-medium">
              {numHits} Treffer
            </span>
          )}
          {numSources > 0 && <span>· {numSources} Zeilen</span>}
        </div>
      </div>
      <button
        onClick={handleCopy}
        title={copied ? "Kopiert!" : "Antwort kopieren"}
        className="opacity-0 group-hover:opacity-100 self-start p-1 rounded-md text-ink-50/50 hover:text-ink hover:bg-cream-200 dark:hover:bg-ink-600 transition"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-brand-500" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function UserAvatar() {
  // Liest username aus localStorage (gleicher Mechanismus wie useAuth).
  const u = (typeof localStorage !== "undefined" ? localStorage.getItem("sb_username") : null) ?? "?";
  return (
    <div className="w-8 h-8 rounded-full bg-ink/85 dark:bg-cream-200 grid place-items-center text-cream-50 dark:text-ink text-xs font-semibold shrink-0">
      {u.charAt(0).toUpperCase()}
    </div>
  );
}
